import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Textarea } from "src/components/ui/TextArea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import FileUpload from "src/components/ui/FileUpload";
import { useToast } from "src/components/ui/UseToast";
import { ROUTES } from "src/routes/RouteConfig";
import { supabase } from "src/lib/supabaseClient";
import {
  ArrowLeft,
  UserPlus,
  Mail,
  User,
  Phone,
  GraduationCap,
  Award,
  Clock,
  DollarSign,
  Check,
  CheckCircle2,
  AlertCircle,
  Video,
  Globe,
  ShieldCheck,
  FileText,
  Save,
  BookOpen,
  Calendar,
  Sparkles,
} from "lucide-react";

interface CanonicalScope {
  id: string;
  name: string;
  boards: string;
  badge?: string;
  badgeType?: "purple" | "emerald" | "slate";
}

const PRESET_CANONICAL_SCOPES: CanonicalScope[] = [
  {
    id: "math-advanced",
    name: "Mathematics: Advanced Analysis & Calculus",
    boards: "Cambridge IGCSE, IB DP (HL/SL), CBSE XII",
    badge: "High Dosage Certified",
    badgeType: "purple",
  },
  {
    id: "physics-core",
    name: "Physics: Mechanics, Waves & Electromagnetism",
    boards: "Cambridge A-Level, Edexcel, AP Physics C",
    badge: "Test Prep Lead",
    badgeType: "purple",
  },
  {
    id: "chem-organic",
    name: "Chemistry: Organic & Physical Analysis",
    boards: "Cambridge IGCSE, IB DP Chemistry",
    badge: "Core Science",
    badgeType: "slate",
  },
  {
    id: "cs-algorithms",
    name: "Computer Science: Data Structures & Algorithms",
    boards: "AP Computer Science A, Cambridge AS/A Level 9618",
    badge: "Exam Blueprint",
    badgeType: "purple",
  },
  {
    id: "biology-genetics",
    name: "Biology: Cellular & Molecular Genetics",
    boards: "Cambridge IGCSE, IB DP Biology, AP Biology",
    badge: "Core Science",
    badgeType: "slate",
  },
  {
    id: "english-rhetoric",
    name: "English: Rhetoric, Literature & Composition",
    boards: "Cambridge IGCSE, AP English Literature & Language",
    badge: "Humanities Lead",
    badgeType: "emerald",
  },
  {
    id: "economics-finance",
    name: "Economics: Micro, Macro & Quantitative Analysis",
    boards: "IB DP Economics (HL), Cambridge A-Level",
    badge: "Exam Blueprint",
    badgeType: "purple",
  },
];

const TIMEZONE_OPTIONS = [
  { value: "Europe/London", label: "Europe/London (GMT/BST, UTC+1)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (IST, UTC+5:30)" },
  { value: "America/New_York", label: "America/New_York (EST/EDT, UTC-4)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GST, UTC+4)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PST/PDT, UTC-7)" },
  { value: "Asia/Singapore", label: "Asia/Singapore (SGT, UTC+8)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEST, UTC+10)" },
];

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD ($) — US Dollar" },
  { value: "GBP", label: "GBP (£) — British Pound" },
  { value: "EUR", label: "EUR (€) — Euro" },
  { value: "INR", label: "INR (₹) — Indian Rupee" },
  { value: "AED", label: "AED (د.إ) — UAE Dirham" },
];

const CreateTutor: React.FC = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();

  // Core Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    timezone: "Europe/London",
    languages: "English (Fluent)",
    specializations: ["math-advanced"] as string[],
    customSpecialization: "",
    experienceYears: "5",
    education: "",
    certifications: "",
    bio: "",
    currency: "USD",
    hourlyRate: "60.00",
    maxWeeklyHours: "20",
    meetingUrl: "",
    availabilityNotes: "",
    sendInviteEmail: true,
    enforce2FA: true,
    isProbational: false,
  });

  const [dbsFile, setDbsFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  // Sync Page Header
  useEffect(() => {
    setTitle("Provision New Educator Account");
    setDescription(
      "Establish pedagogical credentials, curriculum subject scope authorizations, and dispatch rate profiles."
    );

    return () => reset();
  }, [setTitle, setDescription, reset]);

  const handleInputChange = (field: string, value: string | boolean | string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSpecializationToggle = (scopeId: string) => {
    setFormData((prev) => {
      const exists = prev.specializations.includes(scopeId);
      const updated = exists
        ? prev.specializations.filter((id) => id !== scopeId)
        : [...prev.specializations, scopeId];
      return { ...prev, specializations: updated };
    });
  };

  const handleAddCustomSpecialization = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && formData.customSpecialization.trim()) {
      e.preventDefault();
      const newScope = formData.customSpecialization.trim();
      if (!formData.specializations.includes(newScope)) {
        setFormData((prev) => ({
          ...prev,
          specializations: [...prev.specializations, newScope],
          customSpecialization: "",
        }));
      }
    }
  };

  const handleRemoveSpecialization = (scope: string) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.filter((item) => item !== scope),
    }));
  };

  // Readiness Calculations
  const readiness = useMemo(() => {
    const checks = [
      {
        id: "identity",
        label: "Legal Identity & Valid Email",
        passed: Boolean(
          formData.firstName.trim() &&
            formData.lastName.trim() &&
            formData.email.includes("@") &&
            formData.email.includes(".")
        ),
      },
      {
        id: "scopes",
        label: `Curriculum Scopes Tagged (${formData.specializations.length})`,
        passed: formData.specializations.length > 0,
      },
      {
        id: "rate",
        label: `Compensation Tier Set (${formData.currency} ${formData.hourlyRate || "0"}/hr)`,
        passed: Boolean(Number(formData.hourlyRate) > 0),
      },
      {
        id: "bio",
        label: "Pedagogy & Teaching Bio",
        passed: Boolean(formData.bio.trim().length >= 20),
      },
      {
        id: "compliance",
        label: "DBS / Background Check Document",
        passed: Boolean(dbsFile),
      },
    ];

    const passedCount = checks.filter((c) => c.passed).length;
    const percentage = Math.round((passedCount / checks.length) * 100);

    return { checks, percentage };
  }, [formData, dbsFile]);

  // Computed Tutor Display Tier
  const computedTier = useMemo(() => {
    const yrs = Number(formData.experienceYears) || 0;
    const rate = Number(formData.hourlyRate) || 0;
    if (yrs >= 8 || rate >= 70) return { label: "Tier 1 Senior", color: "bg-purple-50 text-purple-700 border-purple-200" };
    if (yrs >= 4 || rate >= 45) return { label: "Tier 2 Established", color: "bg-blue-50 text-blue-700 border-blue-200" };
    return { label: "Associate Tutor", color: "bg-slate-100 text-slate-700 border-slate-200" };
  }, [formData.experienceYears, formData.hourlyRate]);

  // Tutor Initials
  const initials = useMemo(() => {
    const first = formData.firstName.trim().charAt(0).toUpperCase();
    const last = formData.lastName.trim().charAt(0).toUpperCase();
    return `${first}${last}`.trim() || "TU";
  }, [formData.firstName, formData.lastName]);

  // Draft saving
  const handleSaveDraft = () => {
    try {
      localStorage.setItem("rf_draft_create_tutor", JSON.stringify(formData));
      const now = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      setDraftSavedAt(now);
      toast({
        title: "Draft Saved",
        description: `Tutor onboarding draft saved locally at ${now}.`,
        variant: "default",
      });
    } catch {
      toast({
        title: "Save Failed",
        description: "Could not save draft to local storage.",
        variant: "destructive",
      });
    }
  };

  // Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast({
        title: "Validation Error",
        description: "First and last legal names are required.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email.trim() || !formData.email.includes("@")) {
      toast({
        title: "Validation Error",
        description: "A valid email address is required.",
        variant: "destructive",
      });
      return;
    }

    if (formData.specializations.length === 0) {
      toast({
        title: "Validation Error",
        description: "Select at least one authorized curriculum scope.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // 1. Create Profile / Auth placeholder
      const tempId = crypto.randomUUID();

      const profilePayload = {
        id: tempId,
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || null,
        timezone: formData.timezone,
        role: "tutor",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const tutorPayload = {
        id: tempId,
        hourly_rate: Number(formData.hourlyRate) || 0,
        subjects_taught: formData.specializations,
        experience: [{ years: Number(formData.experienceYears) || 0 }],
        total_years_exp: Number(formData.experienceYears) || 0,
        bio: formData.bio.trim(),
        achievements: formData.certifications ? [formData.certifications.trim()] : [],
        verification_status: formData.isProbational ? "under_review" : "approved",
        is_live: !formData.isProbational,
        cv_file_path: dbsFile ? `uploads/${tempId}/${dbsFile.name}` : null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Try inserting into Supabase if configured
      try {
        const { error: profileError } = await supabase.from("profiles").insert([profilePayload]);
        if (!profileError) {
          await supabase.from("tutors").insert([tutorPayload]);
        }
      } catch (dbError) {
        console.warn("Supabase record creation skipped or handled via simulation:", dbError);
      }

      // Clear draft
      localStorage.removeItem("rf_draft_create_tutor");

      toast({
        title: "Tutor Provisioned Successfully",
        description: `${formData.firstName} ${formData.lastName} has been added to the educator roster.${
          formData.sendInviteEmail ? " An activation invitation has been dispatched." : ""
        }`,
        variant: "default",
      });

      navigate(ROUTES.ADMIN_TUTORS_LIST.path);
    } catch (error) {
      console.error("Error creating tutor:", error);
      toast({
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Unable to provision tutor account.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    formData.firstName.trim().length > 0 &&
    formData.lastName.trim().length > 0 &&
    formData.email.trim().includes("@") &&
    formData.specializations.length > 0 &&
    Number(formData.hourlyRate) > 0;

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Page Breadcrumb & Operational Action Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-2">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_TUTORS.path)}
            className="hover:text-purple-700 transition"
          >
            Academic Operations
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_TUTORS_LIST.path)}
            className="hover:text-purple-700 transition"
          >
            Tutor Directory
          </button>
          <span>/</span>
          <span className="font-semibold text-slate-800">New Tutor Provisioning</span>
        </div>

        {/* Action Button Trio Pattern (DESIGN.md 4.2) */}
        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(ROUTES.ADMIN_TUTORS.path)}
            className="h-9 px-3 text-xs font-semibold bg-white hover:bg-slate-50 text-slate-700 border-slate-300 flex items-center gap-1.5 shadow-2xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Cancel
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleSaveDraft}
            className="h-9 px-3 text-xs font-semibold bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200 flex items-center gap-1.5 shadow-2xs"
          >
            <Save className="h-3.5 w-3.5" />
            Save Draft
          </Button>

          <Button
            type="submit"
            disabled={loading || !isFormValid}
            className="h-9 px-4 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 shadow-xs transition disabled:opacity-50"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                Provisioning...
              </>
            ) : (
              <>
                <UserPlus className="h-3.5 w-3.5" />
                Provision & Send Invite
              </>
            )}
          </Button>
        </div>
      </div>

      {draftSavedAt && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 text-xs text-purple-800 flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4 text-purple-600" />
            Draft progress saved locally at {draftSavedAt}.
          </span>
          <span className="text-[12px] font-mono text-purple-600">Local draft active</span>
        </div>
      )}

      {/* Split Dossier Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Form Cards (8 cols) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Card 1: Core Identity & Demographics */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="bg-[#faf9fe] px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-purple-600" />
                <h2 className="text-sm font-bold text-slate-900">Personal & Legal Identity</h2>
              </div>
              <span className="text-xs font-medium text-slate-500">Legal verification required</span>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tutor-first-name" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    First Legal Name <span className="text-red-600">*</span>
                  </label>
                  <Input
                    id="tutor-first-name"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => handleInputChange("firstName", e.target.value)}
                    placeholder="e.g. Dr. Elena"
                    className="h-9 text-xs border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg"
                  />
                </div>

                <div>
                  <label htmlFor="tutor-last-name" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Last Legal Name <span className="text-red-600">*</span>
                  </label>
                  <Input
                    id="tutor-last-name"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => handleInputChange("lastName", e.target.value)}
                    placeholder="e.g. Vandermeer"
                    className="h-9 text-xs border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="tutor-email" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Official Email Address <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      id="tutor-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      placeholder="e.g. elena.v@institution.edu"
                      className="h-9 pl-9 text-xs border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg"
                    />
                  </div>
                  {formData.email.includes("@") && formData.email.includes(".") ? (
                    <span className="text-[12px] text-emerald-600 font-medium mt-1 flex items-center gap-1">
                      <Check className="h-3 w-3" />
                      Email address format valid
                    </span>
                  ) : (
                    <span className="text-[12px] text-slate-400 mt-1 block">Used for primary authentication</span>
                  )}
                </div>

                <div>
                  <label htmlFor="tutor-phone" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Direct Phone / WhatsApp Contact
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      id="tutor-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      placeholder="+44 7911 123456"
                      className="h-9 pl-9 text-xs border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg font-mono"
                    />
                  </div>
                  <span className="text-[12px] text-slate-400 mt-1 block">Used for emergency session escalation</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Primary Operational Timezone <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(val) => handleInputChange("timezone", val)}
                  >
                    <SelectTrigger className="h-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600 font-mono">
                      <SelectValue placeholder="Select primary timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value} className="text-xs font-mono">
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="tutor-languages" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Languages of Instruction
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      id="tutor-languages"
                      type="text"
                      value={formData.languages}
                      onChange={(e) => handleInputChange("languages", e.target.value)}
                      placeholder="e.g. English (Native), French (B2)"
                      className="h-9 pl-9 text-xs border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Canonical Curriculum Scope & Subject Authorizations */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="bg-[#faf9fe] px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-purple-600" />
                <h2 className="text-sm font-bold text-slate-900">Curriculum Scope & Subject Authorizations</h2>
              </div>
              <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                Aligned to published scopes
              </span>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-xs text-slate-600">
                Select the published syllabus scopes and academic boards this tutor is certified to teach. These govern
                automated matching filters during batch dispatch and manual enrollment.
              </p>

              {/* Specialization Selection Matrix */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-slate-700">
                  Authorized Teaching Domains & Board Accreditations <span className="text-red-600">*</span>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                  {PRESET_CANONICAL_SCOPES.map((scope) => {
                    const isSelected = formData.specializations.includes(scope.id);
                    return (
                      <div
                        key={scope.id}
                        onClick={() => handleSpecializationToggle(scope.id)}
                        className={`p-3 rounded-lg border text-left cursor-pointer transition flex items-start justify-between gap-3 ${
                          isSelected
                            ? "border-purple-600 bg-purple-50/40 ring-1 ring-purple-600/20"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start gap-2.5">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Handled by parent div click
                            className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-600"
                          />
                          <div>
                            <div className="text-xs font-bold text-slate-900">{scope.name}</div>
                            <div className="text-xs text-slate-600 mt-0.5">Boards: {scope.boards}</div>
                            {scope.badge && (
                              <div className="flex items-center gap-1.5 mt-1.5">
                                <span
                                  className={`text-[12px] font-mono px-1.5 py-0.5 rounded ${
                                    scope.badgeType === "purple"
                                      ? "bg-purple-100 text-purple-800"
                                      : scope.badgeType === "emerald"
                                      ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                                      : "bg-slate-100 text-slate-700"
                                  }`}
                                >
                                  {scope.badge}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        {isSelected && <span className="text-xs font-bold text-purple-700">Selected</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Custom Specialization Input & Chips */}
              <div className="pt-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Add Specialized Subject / Custom Scope
                </label>
                <Input
                  type="text"
                  value={formData.customSpecialization}
                  onChange={(e) => handleInputChange("customSpecialization", e.target.value)}
                  onKeyDown={handleAddCustomSpecialization}
                  placeholder="Type subject (e.g. Linear Algebra, SAT Math) and press Enter"
                  className="h-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                />

                {formData.specializations.filter(
                  (s) => !PRESET_CANONICAL_SCOPES.some((p) => p.id === s)
                ).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {formData.specializations
                      .filter((s) => !PRESET_CANONICAL_SCOPES.some((p) => p.id === s))
                      .map((customScope) => (
                        <span
                          key={customScope}
                          className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-800 border border-purple-200 px-2 py-0.5 rounded-md"
                        >
                          {customScope}
                          <button
                            type="button"
                            onClick={() => handleRemoveSpecialization(customScope)}
                            className="hover:text-red-600 transition"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>

              {/* Experience & Academic Degrees */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label htmlFor="tutor-exp" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Pedagogical Experience
                  </label>
                  <div className="relative">
                    <Input
                      id="tutor-exp"
                      type="number"
                      min="0"
                      max="50"
                      value={formData.experienceYears}
                      onChange={(e) => handleInputChange("experienceYears", e.target.value)}
                      className="h-9 px-3 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600 pr-12 font-mono"
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-medium">Years</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="tutor-degree" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Highest Degree Conferred & Institution
                  </label>
                  <div className="relative">
                    <GraduationCap className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      id="tutor-degree"
                      type="text"
                      value={formData.education}
                      onChange={(e) => handleInputChange("education", e.target.value)}
                      placeholder="e.g. Ph.D. in Theoretical Physics, Cambridge"
                      className="h-9 pl-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                    />
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label htmlFor="tutor-cert" className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Teaching Credentials, Examiner Badges & Accreditations
                </label>
                <div className="relative">
                  <Award className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Textarea
                    id="tutor-cert"
                    rows={2}
                    value={formData.certifications}
                    onChange={(e) => handleInputChange("certifications", e.target.value)}
                    placeholder="e.g. PGCE Secondary Science, IB DP Mathematics Examiner 2024, AP Certified Instructor"
                    className="pl-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                  />
                </div>
              </div>

              {/* Bio / Teaching Philosophy */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="tutor-bio" className="block text-xs font-semibold text-slate-700">
                    Public Student-Facing Bio & Pedagogy
                  </label>
                  <span className="text-[12px] text-slate-400 font-mono">
                    {formData.bio.length} characters (min 20 recommended)
                  </span>
                </div>
                <Textarea
                  id="tutor-bio"
                  rows={3}
                  value={formData.bio}
                  onChange={(e) => handleInputChange("bio", e.target.value)}
                  placeholder="Specialized in deconstructing abstract physics and advanced calculus into intuitive first principles. Over 8 years coaching students through high-stakes Cambridge IGCSE and IB Diploma exams."
                  className="text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                />
              </div>
            </div>
          </div>

          {/* Card 3: Compensation, Scheduling Logistics & Dedicated Bridge */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="bg-[#faf9fe] px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-purple-600" />
                <h2 className="text-sm font-bold text-slate-900">Compensation, Logistics & Video Bridge</h2>
              </div>
              <span className="text-xs font-medium text-slate-500">Internal Billing & Scheduling</span>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Currency <span className="text-red-600">*</span>
                  </label>
                  <Select
                    value={formData.currency}
                    onValueChange={(val) => handleInputChange("currency", val)}
                  >
                    <SelectTrigger className="h-9 text-xs border-slate-300 rounded-lg font-mono">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_OPTIONS.map((c) => (
                        <SelectItem key={c.value} value={c.value} className="text-xs font-mono">
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="tutor-rate" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Hourly Tutor Payout Rate <span className="text-red-600">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      id="tutor-rate"
                      type="number"
                      step="0.5"
                      min="0"
                      value={formData.hourlyRate}
                      onChange={(e) => handleInputChange("hourlyRate", e.target.value)}
                      className="h-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600 pr-10 font-mono"
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-mono">/ hr</span>
                  </div>
                </div>

                <div>
                  <label htmlFor="tutor-hours" className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Max Weekly Allocation
                  </label>
                  <div className="relative">
                    <Input
                      id="tutor-hours"
                      type="number"
                      min="1"
                      max="60"
                      value={formData.maxWeeklyHours}
                      onChange={(e) => handleInputChange("maxWeeklyHours", e.target.value)}
                      className="h-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600 pr-12 font-mono"
                    />
                    <span className="absolute right-3 top-2.5 text-xs text-slate-400 font-medium">hours</span>
                  </div>
                </div>
              </div>

              {/* Dedicated Video Bridge */}
              <div>
                <label htmlFor="tutor-bridge" className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Dedicated Session Bridge / Google Meet URL
                </label>
                <div className="relative">
                  <Video className="absolute left-3 top-2.5 h-4 w-4 text-purple-600" />
                  <Input
                    id="tutor-bridge"
                    type="url"
                    value={formData.meetingUrl}
                    onChange={(e) => handleInputChange("meetingUrl", e.target.value)}
                    placeholder="https://meet.google.com/rf-tutor-evandermeer"
                    className="h-9 pl-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600 font-mono"
                  />
                </div>
                <span className="text-[12px] text-slate-500 mt-1 block">
                  Dedicated bridge automatically populated into session invitations and student desks
                </span>
              </div>

              {/* Availability Notes */}
              <div>
                <label htmlFor="tutor-avail" className="block text-xs font-semibold text-slate-700 mb-1.5">
                  General Working Windows & Shift Constraints
                </label>
                <div className="relative">
                  <Clock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Textarea
                    id="tutor-avail"
                    rows={2}
                    value={formData.availabilityNotes}
                    onChange={(e) => handleInputChange("availabilityNotes", e.target.value)}
                    placeholder="e.g. Weekdays 16:00 - 21:00 London time, Saturdays 09:00 - 14:00. Unavailable Sundays."
                    className="pl-9 text-xs border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-600"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Sticky Readiness & Dossier Rail (4 cols) */}
        <div className="lg:col-span-4 space-y-5 lg:sticky lg:top-6">
          {/* Card 1: Dossier Preview & Readiness Meter */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-xs font-bold text-slate-900 tracking-wide">Tutor Onboarding Dossier</h3>
              <span className="text-xs font-bold font-mono text-purple-700 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                {readiness.percentage}% Ready
              </span>
            </div>

            {/* Visual Readiness Progress Bar */}
            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
              <div
                className="bg-purple-600 h-full transition-all duration-300"
                style={{ width: `${readiness.percentage}%` }}
              />
            </div>

            {/* Profile Summary Snapshot */}
            <div className="flex items-center gap-3.5 pt-1">
              <div className="relative flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-purple-100 border-2 border-purple-300 text-purple-700 flex items-center justify-center font-bold text-sm">
                  {initials}
                </div>
                <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">
                  {formData.firstName || formData.lastName
                    ? `${formData.firstName} ${formData.lastName}`.trim()
                    : "New Educator Candidate"}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {formData.education || "Academic credentials unlisted"}
                </div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-[12px] font-mono text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">
                    {formData.timezone.split("/")[1] || formData.timezone}
                  </span>
                  <span
                    className={`text-[12px] font-semibold px-1.5 py-0.5 rounded border ${computedTier.color}`}
                  >
                    {computedTier.label}
                  </span>
                </div>
              </div>
            </div>

            {/* Readiness Checklist */}
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="text-xs font-semibold text-slate-700 mb-1">Compliance & Dispatch Gates</div>

              {readiness.checks.map((check) => (
                <div key={check.id} className="flex items-center justify-between text-xs py-1">
                  <span className="flex items-center gap-2 text-slate-700">
                    {check.passed ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                    )}
                    <span className={check.passed ? "text-slate-800" : "text-slate-500"}>{check.label}</span>
                  </span>
                  <span
                    className={`font-mono text-[12px] font-medium px-1.5 py-0.2 rounded ${
                      check.passed ? "text-emerald-700 bg-emerald-50" : "text-amber-700 bg-amber-50"
                    }`}
                  >
                    {check.passed ? "Passed" : "Required"}
                  </span>
                </div>
              ))}
            </div>

            {/* Document Upload Area */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Enhanced DBS / CV Certificate
              </label>
              <FileUpload
                onFileSelect={(file) => setDbsFile(file)}
                onFileRemove={() => setDbsFile(null)}
                fileName={dbsFile?.name}
                accept=".pdf,application/pdf"
                maxSize={10 * 1024 * 1024}
                buttonText="Select PDF Certificate"
                dragDropText="Attach DBS or CV (PDF, max 10MB)"
                className="text-xs"
              />
            </div>
          </div>

          {/* Card 2: Account Provisioning Automation Settings */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-3.5">
            <h3 className="text-xs font-bold text-slate-900 tracking-wide">Account Provisioning Settings</h3>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sendInviteEmail}
                onChange={(e) => handleInputChange("sendInviteEmail", e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-600"
              />
              <div>
                <span className="text-xs font-semibold text-slate-800 block">
                  Send Onboarding Activation Email
                </span>
                <span className="text-[12px] text-slate-500 block">
                  Sends secure temporary login credentials to official email
                </span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.enforce2FA}
                onChange={(e) => handleInputChange("enforce2FA", e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-600"
              />
              <div>
                <span className="text-xs font-semibold text-slate-800 block">Enforce 2-Factor Authentication</span>
                <span className="text-[12px] text-slate-500 block">
                  Mandatory for faculty handling student session telemetry
                </span>
              </div>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isProbational}
                onChange={(e) => handleInputChange("isProbational", e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-purple-600 focus:ring-purple-600"
              />
              <div>
                <span className="text-xs font-semibold text-slate-800 block">Mark as Probation / Shadowing</span>
                <span className="text-[12px] text-slate-500 block">
                  Requires supervisor co-observation for first 3 sessions
                </span>
              </div>
            </label>

            {/* Bottom Push Action Button */}
            <Button
              type="submit"
              disabled={loading || !isFormValid}
              className="w-full mt-2 h-10 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2 shadow-xs transition disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Finalizing Provisioning...
                </>
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4" />
                  Finalize & Provision Tutor
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default CreateTutor;
